import { getErrorState, logger } from '@automata/utils';
import { useRef } from 'react';

import { useGetPlanStatus } from '../apis/wfbApiComponents';

const _usePollWorkflowPlanStatus = ({
  workflowId,
  planId,
}: {
  workflowId?: string;
  planId?: string;
}) => {
  const attemptIndex = useRef<number>(0);

  const getPlanStatusQuery = useGetPlanStatus(
    {
      pathParams: {
        // TODO(IL, 2024-05-30): this NIL fallback is a hack for TypeScript support in React Query v4.
        // When we update to React Query v5, we can use `skipToken` instead of `enable`:
        // https://tanstack.com/query/v5/docs/framework/react/guides/disabling-queries#typesafe-disabling-of-queries-using-skiptoken
        workflowId: workflowId ?? 'NIL',
        planId: planId ?? 'NIL',
      },
    },
    {
      enabled: workflowId !== undefined && planId !== undefined,
      staleTime: Infinity,
      refetchInterval: (data, _query) => {
        if (data === undefined || data.status === 'COMPLETED') {
          attemptIndex.current = 0;
          return false;
        }

        if (data.status === 'PENDING' || data.status === 'UPLOADED') {
          // exponential backoff, same as default implementation of `retryDelay`
          const refetchTime = Math.min(1000 * 2 ** attemptIndex.current, 30000);

          attemptIndex.current += 1;
          return refetchTime;
        }

        if (data.status === 'FAILED') {
          logger.error('Plan failed');
          return false;
        }

        if (data.status === 'CANCELLED') {
          logger.info('Plan canceled');
          return false;
        }

        data.status satisfies never;
        logger.error(`Unexpected plan status value: "${data.status}"`);
        return false;
      },
      refetchIntervalInBackground: true,
    }
  );

  if (getPlanStatusQuery.data?.status.toLowerCase() === 'failed') {
    return {
      ...getErrorState(getPlanStatusQuery.data),
      fetchStatus: 'error',
    };
  } else {
    return getPlanStatusQuery;
  }
};

export default _usePollWorkflowPlanStatus;
